import { useLocaleState, useTranslate } from "ra-core";
import { Link } from "react-router";
import {
  Check,
  MapPin,
  MessageCircle,
  Pencil,
  RotateCcw,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { editOrderPath } from "../amai/paths";
import type { PedidoLista } from "../types";
import { whatsappUrl } from "../amai/whatsapp";
import { mensajeConfirmacion } from "./confirmacion";
import { formatoDinero, mapsUrl } from "./formatoPedido";
import { useCambiarEstado } from "./useCambiarEstado";

// One order in the list: who, how many of each flavor, how much, and the
// actions for its state. A delivered order can go back to pending or be
// cancelled; if it was paid, it asks first and removes the payment.
export const PedidoCard = ({ pedido }: { pedido: PedidoLista }) => {
  const translate = useTranslate();
  const { cambiar, porConfirmar, confirmarCambio, descartar, isPending } =
    useCambiarEstado(pedido);
  const [locale] = useLocaleState();
  // Pending orders only: confirm what was agreed before delivering.
  const confirmar =
    pedido.estado === "pendiente"
      ? whatsappUrl(
          pedido.telefono,
          mensajeConfirmacion(pedido, translate, locale),
        )
      : null;

  const detalle = [
    translate("amai.orders.pieces", { smart_count: pedido.piezas }),
    pedido.total > 0
      ? formatoDinero(pedido.total)
      : translate("amai.orders.no_price"),
    pedido.metodo_pago
      ? translate(`amai.orders.payment_methods.${pedido.metodo_pago}`, {
          _: pedido.metodo_pago,
        })
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <li
      className={cn(
        "flex flex-col gap-2 rounded-xl border bg-card p-4",
        pedido.estado === "cancelado" && "opacity-60",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col">
          <span className="truncate font-semibold">
            {[pedido.cliente, pedido.zona].filter(Boolean).join(" · ")}
          </span>
          <span className="text-sm text-muted-foreground">{detalle}</span>
        </div>
        {pedido.estado === "pendiente" ? null : (
          <Badge
            variant={pedido.estado === "entregado" ? "default" : "outline"}
          >
            {translate(`amai.orders.states.${pedido.estado}`)}
          </Badge>
        )}
      </div>
      <ul className="flex flex-wrap gap-x-3 gap-y-1 text-sm">
        {pedido.sabores.map((sabor) => (
          <li key={sabor.sabor_id} className="flex items-center gap-1">
            <span
              aria-hidden
              className="size-3 rounded-full"
              style={{ background: sabor.color }}
            />
            {sabor.nombre} {sabor.cantidad}
          </li>
        ))}
      </ul>
      {pedido.direccion ? (
        <a
          href={mapsUrl(pedido.direccion, pedido.zona)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={translate("amai.orders.open_maps", {
            address: pedido.direccion,
          })}
          className="flex min-h-11 items-center gap-1 self-start text-sm font-semibold text-primary underline-offset-4 hover:underline break-all"
        >
          <MapPin className="size-4 shrink-0" aria-hidden />
          {pedido.direccion}
        </a>
      ) : null}
      {pedido.notas ? (
        <p className="text-sm text-muted-foreground break-words">
          {pedido.notas}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2 pt-1">
        {pedido.estado === "pendiente" ? (
          <>
            <Button
              className="min-h-11 grow gap-1"
              disabled={isPending}
              onClick={() => cambiar("entregado")}
            >
              <Check className="size-4" />
              {translate("amai.orders.actions.deliver")}
            </Button>
            {confirmar ? (
              <a
                href={confirmar}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  buttonVariants({ variant: "outline" }),
                  "min-h-11 gap-1",
                )}
              >
                <MessageCircle className="size-4" />
                {translate("amai.orders.confirm.button")}
              </a>
            ) : null}
            <Link
              to={editOrderPath(pedido.id)}
              className={cn(
                buttonVariants({ variant: "outline" }),
                "min-h-11 gap-1",
              )}
            >
              <Pencil className="size-4" />
              {translate("amai.orders.actions.edit")}
            </Link>
            <Button
              variant="ghost"
              className="min-h-11 gap-1"
              disabled={isPending}
              onClick={() => cambiar("cancelado")}
            >
              <X className="size-4" />
              {translate("amai.orders.actions.cancel")}
            </Button>
          </>
        ) : porConfirmar ? null : (
          <>
            <Button
              variant="outline"
              className="min-h-11 gap-1"
              disabled={isPending}
              onClick={() => cambiar("pendiente")}
            >
              <RotateCcw className="size-4" />
              {translate("amai.orders.actions.back_to_pending")}
            </Button>
            {pedido.estado === "entregado" ? (
              <Button
                variant="ghost"
                className="min-h-11 gap-1"
                disabled={isPending}
                onClick={() => cambiar("cancelado")}
              >
                <X className="size-4" />
                {translate("amai.orders.actions.cancel")}
              </Button>
            ) : null}
          </>
        )}
      </div>
      {porConfirmar ? (
        <div
          role="alert"
          className="flex flex-col gap-2 rounded-lg bg-secondary p-3"
        >
          <p className="text-sm font-semibold">
            {translate(
              porConfirmar.estado === "cancelado"
                ? "amai.orders.paid_warning.cancel"
                : "amai.orders.paid_warning.pending",
              { amount: formatoDinero(porConfirmar.monto) },
            )}
          </p>
          <div className="flex gap-2">
            <Button
              className="min-h-11 grow"
              disabled={isPending}
              onClick={confirmarCambio}
            >
              {translate("amai.orders.paid_warning.confirm")}
            </Button>
            <Button
              variant="ghost"
              className="min-h-11"
              disabled={isPending}
              onClick={descartar}
            >
              {translate("ra.action.cancel")}
            </Button>
          </div>
        </div>
      ) : null}
    </li>
  );
};
